import React, { useState } from 'react';
import { complianceService } from '../../services/api';

interface ReviewProps {
    establishmentId: string;
    onClose: () => void;
}

const OpeningChecks: React.FC<ReviewProps> = ({ establishmentId, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);

    const [formData, setFormData] = useState({
        fridgeTemp: '',
        freezerTemp: '',
        cleaningCheck: false,
        staffFitToWork: false,
        pestCheck: false,
        notes: '',
        photoKey: '' // Store S3 key after upload
    });

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let photoKey = '';

            // 1. Upload Photo if selected
            if (selectedFile) {
                setUploading(true);
                const { data: presignedData } = await complianceService.getPresignedDocUrl({
                    fileName: selectedFile.name,
                    fileType: selectedFile.type,
                    folder: 'daily-checks'
                });

                await complianceService.uploadToS3(presignedData.data.uploadUrl, selectedFile);
                photoKey = presignedData.data.key;
                setUploading(false);
            }

            // 2. Submit Check
            const today = new Date().toISOString().split('T')[0];

            await complianceService.submitDailyCheck({
                establishmentId,
                date: today,
                type: 'OPENING',
                data: { ...formData, photoKey }, // Include photo key
                performedBy: 'Current User' // TODO: Get from auth
            });

            setSuccess(true);
            setTimeout(onClose, 2000);
        } catch (err) {
            console.error(err);
            setError('Failed to submit checks. Please try again.');
            setUploading(false);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="bg-green-50 p-8 rounded-lg text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-check text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Checks Submitted!</h3>
                <p className="text-gray-600">Your opening checks have been logged successfully.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Opening Checks</h2>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Temperature Checks */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Temperature Checks</h3>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fridge Temperature (°C)</label>
                            <input
                                type="number"
                                step="0.1"
                                required
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                value={formData.fridgeTemp}
                                onChange={(e) => setFormData({ ...formData, fridgeTemp: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Freezer Temperature (°C)</label>
                            <input
                                type="number"
                                step="0.1"
                                required
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                value={formData.freezerTemp}
                                onChange={(e) => setFormData({ ...formData, freezerTemp: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                {/* Hygiene & Safety */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Hygiene & Safety</h3>

                    <div className="space-y-3">
                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                            <input
                                type="checkbox"
                                className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                                checked={formData.cleaningCheck}
                                onChange={(e) => setFormData({ ...formData, cleaningCheck: e.target.checked })}
                            />
                            <span className="text-gray-700 font-medium">Cleaning schedule completed from last night?</span>
                        </label>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                            <input
                                type="checkbox"
                                className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                                checked={formData.staffFitToWork}
                                onChange={(e) => setFormData({ ...formData, staffFitToWork: e.target.checked })}
                            />
                            <span className="text-gray-700 font-medium">All staff fit to work (no illness symptoms)?</span>
                        </label>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                            <input
                                type="checkbox"
                                className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                                checked={formData.pestCheck}
                                onChange={(e) => setFormData({ ...formData, pestCheck: e.target.checked })}
                            />
                            <span className="text-gray-700 font-medium">Pest check completed (no signs of activity)?</span>
                        </label>
                    </div>
                </div>

                {/* Evidence Photo */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Proof of Check (Optional)</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Photo (e.g., Fridge Display)</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                        />
                        {selectedFile && <p className="mt-1 text-sm text-gray-500">Selected: {selectedFile.name}</p>}
                    </div>
                </div>


                {/* Notes */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                    <textarea
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Any issues or maintenance needed..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || uploading}
                        className="px-6 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition disabled:opacity-50 flex items-center"
                    >
                        {loading || uploading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {uploading ? 'Uploading Photo...' : 'Submitting...'}
                            </>
                        ) : 'Submit Checks'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default OpeningChecks;
